// Whether a record belongs to another school than the site it's opened on.
// The site's proxy routes any id to these pages, so a booking (or bundle)
// of school A opened on school B's site must not be shown or acted on
// there. Pure.

/** A school reference as the endpoints give it: an id, or a (populated)
 * school document. */
export type SchoolRef = string | { _id?: unknown } | null | undefined;

/** The id a reference names, if any. */
export function schoolIdOf(ref: SchoolRef | unknown): string | undefined {
  if (typeof ref === "string") return ref.trim() || undefined;
  if (ref && typeof ref === "object" && "_id" in ref) {
    const id = (ref as { _id?: unknown })._id;
    return typeof id === "string" && id.trim() ? id.trim() : undefined;
  }
  return undefined;
}

/** True when the site knows its school (`businessId`), the record names at
 * least one school, and none of them is the site's. An embed without a
 * business id never rejects. */
export function isOtherSchool(businessId: string | undefined, ...refs: Array<SchoolRef | unknown>): boolean {
  const site = businessId?.trim();
  if (!site) return false;
  const ids = refs.map(schoolIdOf).filter((id): id is string => !!id);
  return ids.length > 0 && !ids.includes(site);
}

/** The same check for a record that names its school by slug only (the
 * request-to-book proposal page): true when the site knows its slug, the
 * record names one, and they differ. Case-insensitive; an embed without a
 * slug never rejects. */
export function isOtherSchoolSlug(businessSlug: string | undefined, slug: string | null | undefined): boolean {
  const site = businessSlug?.trim().toLowerCase();
  const theirs = slug?.trim().toLowerCase();
  return !!site && !!theirs && site !== theirs;
}
